import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateLead, type Lead } from "@/hooks/useLeads";

const sourceOptions = [
  { value: "contact_form", label: "Formulário" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "direct", label: "Direto" },
  { value: "admin_manual", label: "Manual (Admin)" },
  { value: "prospeccao", label: "Lead frio" },
];

interface EditLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
}

export function EditLeadDialog({ open, onOpenChange, lead }: EditLeadDialogProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [source, setSource] = useState("contact_form");
  const [projectType, setProjectType] = useState("");
  const [notes, setNotes] = useState("");
  const updateLead = useUpdateLead();

  useEffect(() => {
    if (lead) {
      setName(lead.name);
      setEmail(lead.email ?? "");
      setPhone(lead.phone || "");
      setMessage(lead.message);
      setSource(lead.source || "contact_form");
      setProjectType(lead.project_type || "");
      setNotes(lead.notes || "");
    }
  }, [lead]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lead || !name.trim()) return;

    await updateLead.mutateAsync({
      id: lead.id,
      data: {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        message: message.trim(),
        source,
        project_type: projectType.trim() || null,
        notes: notes.trim() || undefined,
      },
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-lead-nome">Nome *</Label>
              <Input
                id="edit-lead-nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-lead-email">Email</Label>
              <Input
                id="edit-lead-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-lead-telefone">Telefone</Label>
              <Input
                id="edit-lead-telefone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-lead-tipo-de-projeto">Tipo de Projeto</Label>
              <Input
                id="edit-lead-tipo-de-projeto"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                placeholder="Landing Page, App..."
              />
            </div>
          </div>

          {/* Stage is derived from documents (see deriveStage); lost/delivered
              are set from the lead page's ⋯ menu — no manual status here. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-lead-origem">Origem</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger id="edit-lead-origem">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sourceOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-lead-mensagem">Mensagem</Label>
            <Textarea
              id="edit-lead-mensagem"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-lead-notas-internas">Notas internas</Label>
            <Textarea
              id="edit-lead-notas-internas"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Notas visíveis apenas para admins..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={updateLead.isPending}>
              {updateLead.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
